import React, { useMemo } from 'react';
import { ISocialMenu } from '@/repositories/menu/types';
import classNames from 'classnames';
import { Typography } from '@qlub-dev/qlub-kit';
import { interpolateT, useTranslation } from '@clubpay/customer-common-module/src/hook/translations';
import { VersionEnum } from '@clubpay/customer-common-module/src/repository/vendor';
import { getImageString } from '@/services/utils/k_media';
import { checkIsRestaurantOpen } from '@/services/utils/k_date';
import defaultBackground from '@/assets/images/food-pattern.png';
import defaultLogo from '@/assets/images/no-thumbnail-product.png';

import styles from './index.module.scss';

interface IProps {
    index: number;
    item: ISocialMenu;
    ui: VersionEnum | undefined;
    onClick: (item: ISocialMenu, e: any) => void;
}

const QSRRestaurantItem = ({ index, item, ui, onClick }: IProps) => {
    const { t } = useTranslation();

    const isV2 = ui === VersionEnum.V2;

    const isOpen = useMemo(() => {
        return checkIsRestaurantOpen(item.config.openingHour || '', item.config.closingHour || '');
    }, [item.config]);

    const renderLabel = useMemo(() => {
        if (!item.config.closingHour || !item.config.openingHour) {
            return null;
        }

        return (
            <div className={styles.label}>
                <div className={styles.info}>
                    {interpolateT(t('Daily <opening> to <closing>'), {
                        opening: item.config.openingHour,
                        closing: item.config.closingHour,
                    })}
                </div>
                <span
                    className={classNames(styles.chip, {
                        [styles.open]: isOpen,
                    })}
                >
                    {isOpen ? t('Now Open') : t('Closed')}
                </span>
            </div>
        );
    }, [item, isOpen, t]);

    const content = useMemo(() => {
        const image = item.backgroundUrl ? item.backgroundUrl : getImageString(defaultBackground);
        return (
            <div
                className={styles.card}
                onClick={(e) => {
                    onClick(item, e);
                }}
            >
                <div
                    className={classNames(styles.media, { [styles.cover]: item.backgroundUrl })}
                    style={{
                        backgroundImage: `url(${image})`,
                    }}
                >
                    {item.logoSmallUrl && (
                        <div className={classNames(styles.logo, styles.v1)}>
                            <img src={item.logoSmallUrl} alt={item.name} />
                        </div>
                    )}
                </div>
                <div className={styles.content}>
                    <Typography className={styles.name}>{item.name}</Typography>
                    {renderLabel}
                </div>
            </div>
        );
    }, [index, item, onClick, renderLabel]);

    const contentV2 = useMemo(() => {
        const logo = item.logoSmallUrl ? item.logoSmallUrl : getImageString(defaultLogo);
        return (
            <div
                className={styles.card}
                onClick={(e) => {
                    onClick(item, e);
                }}
            >
                <div className={classNames(styles.content, styles.v2)}>
                    <div className={styles.logo}>
                        <img src={logo} alt={item.name} />
                    </div>
                    <div>
                        <Typography className={styles.name}>{item.name}</Typography>
                        {renderLabel}
                    </div>
                </div>
            </div>
        );
    }, [index, item, onClick, renderLabel]);

    return isV2 ? contentV2 : content;
};

export default QSRRestaurantItem;
